import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import useStyles from '../styles/Step2.styles'
import React from 'react'
import Grid from '@mui/material/Grid'
import Autocomplete from '@mui/material/Autocomplete'
import { Controller } from 'react-hook-form'
import Button from '@mui/material/Button'
import { Step2Props } from '../types'
import FormItem from '../../../hoc/FormItem'
import { useSnackbar } from 'notistack'
import HelperModal from '../../../hoc/HelperModal'
import { Alert, Checkbox, CircularProgress, Divider, FormLabel, Typography } from '@mui/material'
import Map from '../components/Map/Map'
import useMapApi from '../components/Map/hooks/useMap.api'
import { LegalObjectTypeList, PhysicalObjectTypeList } from '../constants'
import { useGtmPageView } from '../../hooks'
import TagManager from 'react-gtm-module'

const Step2View: React.FC<Step2Props> = ({
    form,
    conditions,
    options,
    onStepBack,
    map,
    streetSugestions,
    isUpdating,
    activationObject,
}) => {
    const classes = useStyles()
    const { control, formState, handleSubmit } = form.form
    const { errors } = formState
    const mapApi = useMapApi()

    const { enqueueSnackbar } = useSnackbar()
    React.useEffect(() => {
        if (options.error) enqueueSnackbar('Chyba při načítání seznamu PSČ: ' + options.error, { variant: 'error' })
    }, [enqueueSnackbar, options.error])

    useGtmPageView({ pageName: 'Step2', synonym: activationObject?.synonym })
    return (
        <form onSubmit={handleSubmit(form.onSubmit)} method='post' autoComplete='off'>
            <Box p={2} maxWidth={'1050px'}>
                <Box p={2} paddingBottom={0}>
                    <Typography fontSize={{ xs: '1.2rem', sm: '1.973rem' }} variant='h4'>
                        IDENTIFIKACE OBJEKTU
                    </Typography>
                </Box>
                <Box p={2}>
                    <Typography variant='body1' paragraph>
                        Zadejte adresu střeženého objektu a následně klikněte na střed Vašeho objektu pro dotažení přesných GPS
                        souřadnic.
                    </Typography>
                </Box>
                <Divider />
                <Grid container p={1} rowSpacing={{ xs: 1, md: 2 }} columnSpacing={{ xs: 1, sm: 0 }} marginBottom={1}>
                    <Grid container item xs={6} rowSpacing={1}>
                        <Grid item xs={12} md={6}>
                            <FormItem>
                                <FormLabel>PSČ</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormItem>
                                <Controller
                                    name={'objectZIP'}
                                    control={control}
                                    render={({ field }) => (
                                        <Autocomplete
                                            options={options.zipCodes.map((option) => option.label)}
                                            {...field}
                                            defaultValue=''
                                            onChange={(_event, newValue) => {
                                                field.onChange(newValue || '')
                                            }}
                                            filterOptions={(options, state) => {
                                                const trimmed = state.inputValue.replace(' ', '')
                                                return options.filter((opt) => opt.startsWith(trimmed))
                                            }}
                                            noOptionsText='žádné shody'
                                            className={classes.zipAutocomplete}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    type='text'
                                                    value={options.zipCodeSearch}
                                                    onChange={(e) => {
                                                        options.handleZipCodeSearchChanged(e.target.value)
                                                    }}
                                                    error={errors.objectZIP !== undefined}
                                                    helperText={errors.objectZIP?.message}
                                                    label=''
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                    <Grid container item xs={6} rowSpacing={1}>
                        <Grid item xs={12} md={6} paddingLeft={{ md: 1 }}>
                            <FormItem>
                                <FormLabel>Město</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormItem>
                                <Controller
                                    name={'objectAddressID'}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            select={options.cities !== undefined}
                                            disabled={options.cities === undefined}
                                            error={errors.objectCity !== undefined}
                                            helperText={errors.objectCity?.message}
                                            label=''
                                        >
                                            {options.cities &&
                                                options.cities.map((option, index) => (
                                                    <MenuItem key={index} value={option.id}>
                                                        {option.name}
                                                    </MenuItem>
                                                ))}
                                        </TextField>
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container p={1} rowSpacing={{ xs: 1, md: 2 }} columnSpacing={{ xs: 1, sm: 0 }} marginBottom={1}>
                    <Grid container item rowSpacing={1}>
                        <Grid item xs={12} md={3} display={'inline'}>
                            <FormItem>
                                <FormLabel>Ulice</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <FormItem>
                                <Controller
                                    name={'objectStreet'}
                                    control={control}
                                    render={({ field }) => (
                                        <Autocomplete
                                            options={streetSugestions.streets.map((option) => option)}
                                            {...field}
                                            onChange={(_event, newValue) => {
                                                field.onChange(newValue || '')
                                            }}
                                            onBlur={() => map.handleAddressChanged()}
                                            disableClearable
                                            noOptionsText={!streetSugestions.isLoading ? 'žádné shody' : undefined}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    type='text'
                                                    value={streetSugestions.streetSearch}
                                                    onChange={(e) => {
                                                        streetSugestions.handleStreetSearchChanged(e.target.value)
                                                    }}
                                                    error={errors.objectStreet !== undefined}
                                                    helperText={errors.objectStreet?.message}
                                                    label=''
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                    <Grid container item xs={6} rowSpacing={1}>
                        <Grid item xs={12} md={6} display={'inline'}>
                            <FormItem>
                                <FormLabel>Číslo popisné</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormItem>
                                <Controller
                                    name={'objectCodeNumber'}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            id='RegNo'
                                            {...field}
                                            onBlur={() => map.handleAddressChanged()}
                                            disabled={!conditions.enableStreetNumber}
                                            error={errors.objectCodeNumber !== undefined}
                                            helperText={errors.objectCodeNumber?.message}
                                            fullWidth
                                            label=''
                                            variant='outlined'
                                        />
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                    <Grid container item xs={6} rowSpacing={1}>
                        <Grid item xs={12} md={6} display={'inline'} paddingLeft={{ md: 1 }}>
                            <FormItem>
                                <FormLabel>Číslo orientační</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormItem>
                                <Controller
                                    name={'objectGuidanceNumber'}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            onBlur={() => map.handleAddressChanged()}
                                            disabled={!conditions.enableStreetNumber}
                                            error={errors.objectGuidanceNumber !== undefined}
                                            helperText={errors.objectGuidanceNumber?.message}
                                            fullWidth
                                            label=''
                                            variant='outlined'
                                        />
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                    <Grid container item xs={6} rowSpacing={1}>
                        <Grid item xs={12} md={6} display={'inline'}>
                            <FormItem>
                                <FormLabel>Parcelní číslo</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormItem>
                                <Controller
                                    name={'objectParcelNumber'}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            error={errors.objectParcelNumber !== undefined}
                                            disabled={!conditions.enableParcel}
                                            helperText={errors.objectParcelNumber?.message}
                                            fullWidth
                                            variant='outlined'
                                        />
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                    <Grid container item xs={6} rowSpacing={1}>
                        <Grid item xs={12} md={6} display={'inline'} paddingLeft={{ md: 1 }}>
                            <FormItem>
                                <FormLabel>Evidenční číslo</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormItem>
                                <Controller
                                    name={'objectRegistrationNumber'}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            onBlur={() => map.handleAddressChanged()}
                                            disabled={!conditions.enableRegistrationNumber}
                                            error={errors.objectRegistrationNumber !== undefined}
                                            helperText={errors.objectRegistrationNumber?.message}
                                            fullWidth
                                            label=''
                                            variant='outlined'
                                        />
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                </Grid>
                {mapApi.scriptsLoaded && (
                    <Grid container p={1} spacing={{ xs: 1, md: 2 }} marginBottom={1}>
                        <Grid item xs={12} md={12}>
                            <FormItem>
                                <Map
                                    mapType={google.maps.MapTypeId.ROADMAP}
                                    mapTypeControl={true}
                                    zoom={map.zoom}
                                    center={map.center}
                                    onCenterChanged={(center) => map.handleLatLngChanged(center)}
                                ></Map>
                            </FormItem>
                        </Grid>
                    </Grid>
                )}
                {map.showWarningForGpsChange && (
                    <Grid container p={1} spacing={{ xs: 1, md: 2 }} marginBottom={1}>
                        <Grid item xs={12} md={12}>
                            <Alert severity='warning'>
                                <Typography> Byly změněny GPS souřadnice, změnila se i ulice?</Typography>
                            </Alert>
                        </Grid>
                    </Grid>
                )}
                {map.center && (
                    <Grid container p={1} spacing={{ xs: 1, md: 2 }} marginBottom={1}>
                        <Grid item xs={12} md={12}>
                            <Controller
                                name={'confirmObjectGPS'}
                                control={form.form.control}
                                render={({ field }) => (
                                    <Alert
                                        severity={
                                            errors.confirmObjectGPS !== undefined ? 'error' : field.value ? 'success' : 'warning'
                                        }
                                        className={classes.gpsConfirmAlert}
                                    >
                                        <Typography component='span'>Prosím, zkontrolujte, zda červená značka</Typography>
                                        <img src='/marker.png' alt='marker' />
                                        <Typography component='span'>
                                            na mapě označuje Váš střežený objekt. Pokud tomu tak není, přesuňte značku pomocí myši
                                            na střed objektu a následně potvrďte
                                        </Typography>
                                        <Checkbox disabled={map.isLoading} {...field} checked={field.value} color='primary' />
                                    </Alert>
                                )}
                            />
                        </Grid>
                    </Grid>
                )}
                <Grid container p={1} spacing={{ xs: 1, md: 2 }} marginBottom={2}>
                    <Grid className={classes.longLabelText} container item xs={12} rowSpacing={1}>
                        <Grid item xs={12} md={3}>
                            <FormItem marginTop={{ xs: '10px', md: 0 }}>
                                <FormLabel>Garantovaný čas dojezdu jednotky</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <FormItem>
                                <TextField
                                    fullWidth
                                    disabled
                                    value={conditions.guaranteedTime || 'není garantován dojezdový čas'}
                                    variant='outlined'
                                    className={!conditions.guaranteedTime ? classes.guaranteedTimeTextboxWarning : undefined}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: <HelperModal textKey='GUARANTEED_TIME_ARRIVE' />,
                                    }}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12} rowSpacing={1}>
                        <Grid item xs={12} md={3}>
                            <FormItem>
                                <FormLabel>Typ objektu</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <FormItem>
                                {conditions.personType === 'Legal' && (
                                    <Controller
                                        name={'objectType'}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                id='outlined-select-currency'
                                                select
                                                fullWidth
                                                error={errors.objectType !== undefined}
                                                helperText={errors.objectType?.message}
                                                {...field}
                                            >
                                                {LegalObjectTypeList.map((option) => (
                                                    <MenuItem key={option.label} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                )}
                                {conditions.personType === 'Physical' && (
                                    <Controller
                                        name={'objectType'}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                id='outlined-select-currency'
                                                select
                                                fullWidth
                                                error={errors.objectType !== undefined}
                                                helperText={errors.objectType?.message}
                                                {...field}
                                            >
                                                {PhysicalObjectTypeList.map((option) => (
                                                    <MenuItem key={option.label} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                )}
                            </FormItem>
                        </Grid>
                    </Grid>
                    {conditions.enableAnotherObjectType && (
                        <Grid container item>
                            <Grid item xs={12} md={3}>
                                <FormItem>
                                    <FormLabel>Upřesnění jiného objektu</FormLabel>
                                </FormItem>
                            </Grid>
                            <Grid item xs={12} md={9}>
                                <FormItem>
                                    <Controller
                                        name={'objectTypeAnother'}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                error={errors.objectTypeAnother !== undefined}
                                                helperText={errors.objectTypeAnother?.message}
                                                fullWidth
                                                label=''
                                                variant='outlined'
                                            />
                                        )}
                                    />
                                </FormItem>
                            </Grid>
                        </Grid>
                    )}

                    <Grid container item>
                        <Grid item xs={12} md={3}>
                            <FormItem>
                                <FormLabel>Popis objektu</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <FormItem>
                                <Controller
                                    name={'objectDesription'}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            error={errors.objectDesription !== undefined}
                                            helperText={options.objectDescriptionHelper}
                                            fullWidth
                                            label=''
                                            variant='outlined'
                                        />
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>

                    {conditions.enableObjectSize && (
                        <>
                            <Grid item xs={4} sm={8} md={3}>
                                <FormItem>
                                    <FormLabel>
                                        Velikost střežené plochy v objektu&nbsp;
                                        <HelperModal textKey='OBJECT_SIZE' />
                                    </FormLabel>
                                </FormItem>
                            </Grid>
                            <Grid item xs={4} sm={8} md={9}>
                                <FormItem>
                                    <Controller
                                        name={'objectSize'}
                                        control={control}
                                        render={({ field }) => (
                                            <RadioGroup row {...field} aria-label='gender' name='row-radio-buttons-group'>
                                                <FormControlLabel
                                                    value='LessOrEqual500'
                                                    control={<Radio color='primary' />}
                                                    label='Do 500 m²'
                                                />
                                                <FormControlLabel
                                                    value='More500'
                                                    control={<Radio color='primary' />}
                                                    label='Nad 500 m²'
                                                />
                                            </RadioGroup>
                                        )}
                                    />
                                </FormItem>
                                {errors.objectSize && (
                                    <Box fontSize={'0.875rem'} color={'red'}>
                                        {errors.objectSize?.message}
                                    </Box>
                                )}
                            </Grid>
                        </>
                    )}
                </Grid>
                <Box paddingTop={2} className={classes.flex}>
                    <Button variant='outlined' onClick={() => onStepBack()} color='secondary'>
                        &#60; Zpět
                    </Button>
                    <Button
                        variant='contained'
                        color='primary'
                        type='submit'
                        disabled={isUpdating}
                        startIcon={isUpdating && <CircularProgress size={20} />}
                        onClick={() => {
                            TagManager.dataLayer({
                                dataLayer: {
                                    event: 'clickNext',
                                },
                            })
                        }}
                    >
                        Pokračovat &#62;
                    </Button>
                </Box>
            </Box>
        </form>
    )
}

export default Step2View
